import { PALETTE } from './palette';

export interface Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  /** CSS-pixel size of the drawing surface. Never read canvas.width/height directly. */
  readonly width: number;
  readonly height: number;
  resize(): void;
  beginFrame(): void;
  endFrame(): void;
  destroy(): void;
}

export function createRenderer(mount: HTMLElement): Renderer {
  const canvas = document.createElement('canvas');
  mount.appendChild(canvas);

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('2d context unavailable');

  let width = 0;
  let height = 0;

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    width  = mount.clientWidth  || window.innerWidth;
    height = mount.clientHeight || window.innerHeight;
    canvas.width  = Math.round(width  * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width  = `${width}px`;
    canvas.style.height = `${height}px`;
    // Draw in CSS pixels; DPR handled entirely by the transform.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  window.addEventListener('resize', resize);
  resize();

  const beginFrame = () => {
    ctx.fillStyle = PALETTE.void;
    ctx.fillRect(0, 0, width, height);
  };

  const endFrame = () => {
    // Intentionally empty: kept as a symmetry hook for M5 post-pass effects
    // (shake transform restore, composite layers). No frame-state latch —
    // a latch would wedge permanently if a caller ever threw between begin/end.
  };

  const destroy = () => {
    window.removeEventListener('resize', resize);
    canvas.remove();
  };

  return {
    canvas,
    ctx,
    get width()  { return width; },
    get height() { return height; },
    resize,
    beginFrame,
    endFrame,
    destroy,
  };
}
